import React, { useState, useEffect } from "react";
import "./ScenariosPage.css";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import { Scenario, ScenarioType, DifficultyLevel, PersonaType } from "../../types";
import { SidePanel } from "../shared/SidePanel";
import { FormField, Button } from "../shared/FormField";

const TYPES: ScenarioType[] = ["cold_call", "discovery", "objection_handling"];
const DIFFICULTIES: DifficultyLevel[] = ["beginner", "intermediate", "advanced"];
const PERSONAS: PersonaType[] = ["skeptical", "technical", "executive"];

export function ScenariosPage() {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<Scenario | null>(null);
  const [form, setForm] = useState({ name: "", description: "", type: "cold_call" as ScenarioType, difficulty: "beginner" as DifficultyLevel, personas: [...PERSONAS], goal: "", successCriteria: "" as string, context: "", businessContext: "", requirements: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadScenarios(); }, []);

  const loadScenarios = async () => {
    try {
      const result = await apiGet<{ items: Scenario[] }>("/api/scenarios");
      setScenarios(result.items);
    } catch (err) {
      console.error("Failed to load scenarios:", err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", type: "cold_call", difficulty: "beginner", personas: [...PERSONAS], goal: "", successCriteria: "", context: "", businessContext: "", requirements: "" });
    setPanelOpen(true);
  };

  const openEdit = (s: Scenario) => {
    setEditing(s);
    setForm({ name: s.name, description: s.description, type: s.type, difficulty: s.difficulty, personas: [...s.personas], goal: s.goal || "", successCriteria: (s.successCriteria || []).join("\n"), context: s.context || "", businessContext: s.businessContext || "", requirements: s.requirements || "" });
    setPanelOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        successCriteria: form.successCriteria.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (editing) {
        await apiPut(`/api/scenarios/${editing.scenarioId}`, payload);
      } else {
        await apiPost("/api/scenarios", payload);
      }
      setPanelOpen(false);
      await loadScenarios();
    } catch (err) {
      console.error("Failed to save scenario:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this scenario?")) return;
    try {
      await apiDelete(`/api/scenarios/${id}`);
      await loadScenarios();
    } catch (err) {
      console.error("Failed to delete scenario:", err);
    }
  };

  const togglePersona = (p: PersonaType) => {
    setForm((prev) => ({
      ...prev,
      personas: prev.personas.includes(p) ? prev.personas.filter((x) => x !== p) : [...prev.personas, p],
    }));
  };

  if (loading) return <div className="protrain-admin-loading">Loading scenarios...</div>;

  return (
    <div className="protrain-scenarios-page">
      <div className="protrain-admin-header">
        <h2 className="protrain-page-title">Scenarios</h2>
        <Button onClick={openCreate}>+ New Scenario</Button>
      </div>

      <div className="protrain-scenarios-grid">
        {scenarios.map((s) => (
          <div key={s.scenarioId} className="protrain-scenarios-card">
            <div className="protrain-scenarios-card-top">
              <span className="protrain-scenarios-card-type">{s.type.replace(/_/g, " ")}</span>
              <span className={`protrain-scenarios-card-status ${s.isActive ? "protrain-scenarios-card-status--active" : ""}`}>
                {s.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <h3 className="protrain-scenarios-card-name">{s.name}</h3>
            <p className="protrain-scenarios-card-desc">{s.description}</p>
            <div className="protrain-scenarios-card-meta">
              <span>Difficulty: {s.difficulty}</span>
              <span>Personas: {s.personas.length}</span>
            </div>
            <div className="protrain-scenarios-card-context">
              {s.businessContext?.trim() && <span className="protrain-scenarios-chip">Business context</span>}
              {s.requirements?.trim() && <span className="protrain-scenarios-chip">Requirements</span>}
              {s.goal?.trim() && <span className="protrain-scenarios-chip">Goal</span>}
              {s.successCriteria?.length > 0 && <span className="protrain-scenarios-chip">{s.successCriteria.length} success {s.successCriteria.length === 1 ? "criterion" : "criteria"}</span>}
              {!s.businessContext?.trim() && !s.requirements?.trim() && !s.goal?.trim() && !(s.successCriteria?.length > 0) && (
                <span className="protrain-scenarios-chip protrain-scenarios-chip--empty">No persona context</span>
              )}
            </div>
            <div className="protrain-scenarios-card-actions">
              <Button variant="secondary" onClick={() => openEdit(s)}>Edit</Button>
              <Button variant="danger" onClick={() => handleDelete(s.scenarioId)}>Delete</Button>
            </div>
          </div>
        ))}
        {scenarios.length === 0 && <div className="protrain-admin-empty">No scenarios yet. Create one to get started.</div>}
      </div>

      <SidePanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={editing ? "Edit Scenario" : "New Scenario"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPanelOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.name.trim()}>
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </>
        }
      >
        <FormField label="Name" htmlFor="scenario-name">
          <input id="scenario-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FormField>
        <FormField label="Description" htmlFor="scenario-desc">
          <textarea id="scenario-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </FormField>
        <FormField label="Type" htmlFor="scenario-type">
          <select id="scenario-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as ScenarioType })}>
            {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
        </FormField>
        <FormField label="Difficulty" htmlFor="scenario-diff">
          <select id="scenario-diff" value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value as DifficultyLevel })}>
            {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </FormField>
        <FormField label="Personas">
          <div className="protrain-scenarios-persona-toggles">
            {PERSONAS.map((p) => (
              <label key={p} className="protrain-scenarios-persona-toggle">
                <input type="checkbox" checked={form.personas.includes(p)} onChange={() => togglePersona(p)} />
                <span>{p}</span>
              </label>
            ))}
          </div>
        </FormField>
        <FormField label="Prospect's Business Context" htmlFor="scenario-business">
          <textarea id="scenario-business" value={form.businessContext} onChange={(e) => setForm({ ...form, businessContext: e.target.value })} placeholder="Who the prospect is and what their business looks like — industry, size, current tools, pressures. e.g., Mid-market logistics firm, 450 employees, just lost two big accounts, CFO is cutting costs." />
        </FormField>
        <FormField label="Prospect's Requirements" htmlFor="scenario-requirements">
          <textarea id="scenario-requirements" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} placeholder="What the prospect needs or is evaluating for — priorities, must-haves, deal-breakers. e.g., Needs SOC 2, must integrate with Salesforce, wants ROI within 2 quarters." />
        </FormField>
        <FormField label="Goal" htmlFor="scenario-goal">
          <input id="scenario-goal" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="e.g., Book a 30-minute discovery meeting" />
        </FormField>
        <FormField label="Success Criteria (one per line)" htmlFor="scenario-criteria">
          <textarea id="scenario-criteria" value={form.successCriteria} onChange={(e) => setForm({ ...form, successCriteria: e.target.value })} placeholder={"Opened with a relevant hook\nIdentified a pain point\nProposed a specific next step\nHandled at least one objection"} />
        </FormField>
        <FormField label="Additional Context" htmlFor="scenario-context">
          <textarea id="scenario-context" value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} placeholder="e.g., The prospect's company just raised Series B. They're scaling their sales team from 20 to 50 reps." />
        </FormField>
      </SidePanel>
    </div>
  );
}
